import {Injectable} from '@angular/core';
import {BaseApiService} from '../base-api.service';

/** How the person importing draws: the defaults a file's unstyled shapes get. */
export interface ImportStyle {
  lineColor: string;
  lineWidth: number;
  lineStyle: string;
  fillColor: string;
  /** The setting is a transparency: a shape gets 1 minus it. */
  fillTransparency: number;
  showDistances: boolean;
  showPolygonName: boolean;
  showPolygonArea: boolean;
  showPolygonPerimeter: boolean;
  showSpotName: boolean;
  spotIconColor: string;
  default3dAltitude: number;
}

/** What an import does with what is on the map: adds to it, or replaces it once the files are in. */
export type ImportMode = 'add' | 'replace';

export interface ImportBatchRequest {
  mode: ImportMode;
  files: {name: string, size: number}[];
  language: string;
  style: ImportStyle;
  /** A Replace whose restore point could not be taken, gone ahead with anyway. */
  withoutRestorePoint?: boolean;
}

export interface ImportBatchResponse {
  batch: {id: string, mode: ImportMode, restorePointId: string | null};
  /** A job per file, in order; one with no path failed at once (`error` says why). */
  jobs: {id: string, fileName: string, path: string | null, status: string, error: string | null}[];
}

export interface ImportPreview {
  /** Stored documents, as the import would write them. */
  geometries: any[];
  groups: any[];
  layers: any[];
  items: number;
  skipped: {[kind: string]: number};
  project: {taxRate?: number, clientName?: string, defaultProductId?: string} | null;
}

/**
 * Importing files is the server's, as for every app: the app names the files
 * and what to do with the map (a batch), uploads each where it is told and
 * says so; the server reads and writes them on its own and everyone on the
 * project follows it (projects/{pid}/importBatches, importJobs; see
 * ImportJobsService).
 */
@Injectable({
  providedIn: 'root',
})
export class ImportApiService extends BaseApiService {

  createBatch(projectId: string, request: ImportBatchRequest) {
    return this.post<ImportBatchResponse>(`/import/${projectId}/batches`, request);
  }

  /** How far an upload got (every 15 s or so), that it is there, or that it stopped. */
  reportUpload(projectId: string, jobId: string, report: {state: 'uploading', uploaded: number} | {state: 'uploaded' | 'failed'}) {
    return this.put<{status: string}>(`/import/${projectId}/jobs/${jobId}`, report);
  }
}

/**
 * The demo's import: a small file read without being added anywhere (POST
 * /import/preview). On the 4 GB function, as it reads the file there.
 */
@Injectable({
  providedIn: 'root',
})
export class ImportPreviewApiService extends BaseApiService {
  protected override BASE_URL = BaseApiService.HEAVY_BASE_URL;

  preview(fileName: string, base64: string, language: string, style: ImportStyle) {
    return this.post<ImportPreview>('/import/preview', {fileName, data: base64, language, style});
  }
}
